import React, { useEffect, useState } from 'react';
import { message, Page, theme, Button } from 'components';
import styled from 'styled-components';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { getListing, isCoopAgent } from 'api/ListingController';
import errorReporter from '@app/errorReporter';
import axios from 'axios';
import AppointmentPropertyUpdate, {
  NotificationOptions,
} from '@app/DashboardComponents/AppointmentPropertyUpdate';
import { featureEnabled } from 'utils';
import { Loading } from '../MyBusinessPage/components';

const PropertyUpdatePage = () => {
  const { listingId } = useParams<{ listingId: string }>();

  const [listing, setListing] = useState(null);
  const history = useHistory();
  const [notificationOptions, setNotificationOptions] =
    useState<NotificationOptions | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!featureEnabled('propertyNotificationsRevampFeature', 1)) {
          const isCoopAgentRes = await isCoopAgent(listingId);
          if (!isCoopAgentRes.hasBookedShowing) {
            history.goBack();
          }
        }
        const res = await getListing(listingId);
        const { data: newOptions } = await axios.get<NotificationOptions>(
          `/user/current/listing_notification_setting/${listingId}`,
        );
        setNotificationOptions(newOptions);
        setListing(res);
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const onNotificationOptionsChange = (
    options: Partial<NotificationOptions>,
  ) => {
    const newOptions = {
      ...notificationOptions,
      ...options,
    };

    setNotificationOptions(newOptions);
  };

  const saveNotificationOptions = async () => {
    setIsLoading(true);
    try {
      const { data: updatedOptions } = await axios.post<NotificationOptions>(
        `/user/current/listing_notification_setting/${listingId}`,
        notificationOptions,
      );

      setNotificationOptions(updatedOptions);
      setIsLoading(false);
      message.success(
        'Notification preferences on the listing have been updated successfully',
      );
    } catch (err) {
      errorReporter.error(err);
      setIsLoading(false);
      message.error('Failed to update notification preferences on the listing');
    }
  };

  if (isLoading || (!listing && !notificationOptions)) {
    return <Loading />;
  }

  const pageTitle = featureEnabled('propertyNotificationsRevampFeature', 1)
    ? 'Notifications'
    : 'Updates';
  return (
    <Page type="hollow">
      <Layout>
        <>
          <StyledHeaderContainer>
            <Heading>{`${listing?.line1} ${
              listing?.line2 || ''
            } - Property ${pageTitle}`}</Heading>
            {featureEnabled('propertyNotificationsRevampFeature', 1) && (
              <StyledButtonContainer>
                <Button
                  type="primary"
                  onClick={saveNotificationOptions}
                  disabled={isLoading}
                >
                  Save
                </Button>
              </StyledButtonContainer>
            )}
          </StyledHeaderContainer>
          <Border />
          <AppointmentPropertyUpdate
            isLoading={isLoading}
            newNotifications={notificationOptions}
            listingAddress={`${listing?.line1} ${listing?.line2 || ''}`}
            onNotificationOptionsChange={onNotificationOptionsChange}
            saveNotifications={saveNotificationOptions}
          />
        </>
      </Layout>
    </Page>
  );
};

export default PropertyUpdatePage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: auto;
  padding: 20px;
  width: 100%;
  background: white;
`;

const Heading = styled.h2`
  margin: 0;
  font-weight: 300;
  color: ${theme.gray7};
  font-size: 30px;
`;

const Border = styled.hr`
  width: 100%;
  margin: 10px 0px;
  border: 1px solid ${theme.gray4};
`;

const StyledHeaderContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto;
`;

const StyledButtonContainer = styled.div`
  justify-content: right;
  margin-top: 10px;
`;
